import {
	DatabaseViewFilterOperator,
	DatabaseViewRequest,
	DateRangeRequest,
} from '../../../../api/api-types/database-view-request.api';
import { DatabaseViewSchema } from '../../../../api/app-types/database-view.app';
import { DatabaseSortDirection } from '../models/database-list.types';

// Advisor filters match on the advisor id with `equals`. The search term is a display name, so it is never sent.
const ID_EQUALS_FIELD_IDS = new Set<string>(['advisor', 'advisorName']);

const OPEN_END_DATE = '9999-12-31';
const OPEN_END_ISO = '9999-12-31T23:59:59.999Z';

export interface DatabaseListQueryState<TFilterKey extends string, TSortField extends string> {
	rangeStart: string;
	rangeEnd: string;
	filters: Record<TFilterKey, string[]>;
	filterSearch: Record<TFilterKey, string>;
	sortField: TSortField | null;
	sortDirection: DatabaseSortDirection;
	dateRanges?: Partial<Record<string, { start: string; end: string }>>;
}

export interface DatabaseRequestMap<TFilterKey extends string, TSortField extends string> {
	filterParamMap: Partial<Record<TFilterKey, string>>;
	sortParamMap: Partial<Record<TSortField, string>>;
	dateRangeFieldIds?: readonly string[];
}

export interface DatabaseRequestBuildOptions {
	selectedColumns: string[];
	schema?: DatabaseViewSchema | null;
}

export function buildDatabaseListRequest<TFilterKey extends string, TSortField extends string>(
	state: DatabaseListQueryState<TFilterKey, TSortField>,
	mapping: DatabaseRequestMap<TFilterKey, TSortField>,
	options: DatabaseRequestBuildOptions,
): DatabaseViewRequest {
	const dateRange = buildDateRange(state.rangeStart, state.rangeEnd);
	const payload: DatabaseViewRequest = {
		selectedColumns: [...new Set(options.selectedColumns.filter((value) => value.trim().length > 0))],
		...(dateRange ? { dateRange } : {}),
	};
	validateSelectedColumns(options.schema, payload.selectedColumns);

	for (const key of Object.keys(state.filters) as TFilterKey[]) {
		const selectedValues = state.filters[key] ?? [];
		const fieldId = mapping.filterParamMap[key];
		if (!fieldId) continue;
		const isIdField = ID_EQUALS_FIELD_IDS.has(fieldId);
		const searchTerm = state.filterSearch[key]?.trim() ?? '';
		const values = isIdField ? selectedValues : selectedValues.length ? selectedValues : searchTerm ? [searchTerm] : [];
		if (!values.length) continue;
		const operator = isIdField
			? 'equals'
			: resolveFilterOperator(options.schema, fieldId, selectedValues.length ? 'in' : 'contains');
		assertSchemaOperation(options.schema, fieldId, operator, false);
		(payload.filters ??= []).push({ fieldId, operator, values });
	}

	for (const fieldId of mapping.dateRangeFieldIds ?? []) {
		const columnRange = state.dateRanges?.[fieldId];
		if (!columnRange?.start) continue;
		if (!hasBetweenOperation(options.schema, fieldId)) continue;
		const min = asDateOnly(columnRange.start);
		const max = columnRange.end ? asDateOnly(columnRange.end) : OPEN_END_DATE;
		if (min > max) throw new Error(`Invalid database view date range: ${fieldId}`);
		(payload.filters ??= []).push({ fieldId, operator: 'between', values: [min, max], valueType: 'date' });
	}

	const sortFieldId = state.sortField ? mapping.sortParamMap[state.sortField] : undefined;
	if (sortFieldId) {
		assertSchemaOperation(options.schema, sortFieldId, undefined, true);
		payload.sort = { fieldId: sortFieldId, ascending: state.sortDirection === 'asc' };
	}

	return payload;
}

function resolveFilterOperator(
	schema: DatabaseViewSchema | null | undefined,
	fieldId: string,
	preferred: DatabaseViewFilterOperator,
): DatabaseViewFilterOperator {
	if (!schema) return preferred;
	const column = schema.columns.find((candidate) => candidate.id === fieldId);
	if (!column) return preferred;
	if (column.filterOperations.includes(preferred)) return preferred;
	if (column.filterOperations.includes('contains')) return 'contains';
	if (column.filterOperations.includes('equals')) return 'equals';
	return preferred;
}

function assertSchemaOperation(
	schema: DatabaseViewSchema | null | undefined,
	fieldId: string,
	operator: DatabaseViewFilterOperator | undefined,
	sortable: boolean,
): void {
	if (!schema) return;
	const column = schema.columns.find((candidate) => candidate.id === fieldId);
	if (!column || (sortable ? !column.sortable : !operator || !column.filterOperations.includes(operator))) {
		throw new Error(`Unsupported database view operation: ${fieldId}`);
	}
}

export function validateSelectedColumns(
	schema: DatabaseViewSchema | null | undefined,
	selectedColumns: string[],
): void {
	if (!schema) return;
	const availableColumns = new Set(schema.columns.map((column) => column.id));
	if (selectedColumns.some((column) => !availableColumns.has(column))) {
		throw new Error('Database view schema is missing a selected column');
	}
}

function hasBetweenOperation(schema: DatabaseViewSchema | null | undefined, fieldId: string): boolean {
	if (!schema) return false;
	const column = schema.columns.find((candidate) => candidate.id === fieldId);
	return !!column && column.filterOperations.includes('between');
}

function buildDateRange(start: string, end: string): DateRangeRequest | undefined {
	const min = start ? asStartOfDayIso(start) : undefined;
	const max = end ? asEndOfDayIso(end) : OPEN_END_ISO;

	if (!min || !max) return undefined;
	if (end && min > max) throw new Error('Invalid database view date range');

	return { min, max };
}

function asDateOnly(date: string): string {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
	const parsed = match ? new Date(Date.UTC(+match[1], +match[2] - 1, +match[3])) : null;
	if (!match || !parsed || parsed.getUTCDate() !== +match[3]) throw new Error(`Invalid database view date: ${date}`);
	return date;
}

function asStartOfDayIso(date: string): string {
	return toUtcIso(date, 0, 0, 0, 0);
}

function asEndOfDayIso(date: string): string {
	return toUtcIso(date, 23, 59, 59, 999);
}

function toUtcIso(date: string, hours: number, minutes: number, seconds: number, ms: number): string {
	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
	if (!match) throw new Error(`Invalid database view date: ${date}`);
	const [year, month, day] = match.slice(1).map(Number);
	const parsed = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds, ms));
	if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) {
		throw new Error(`Invalid database view date: ${date}`);
	}
	return parsed.toISOString();
}
